export interface Logo {
  altText?: string;
  asset?: Asset;
}

export enum InventoryStatus {
  InStock = 'IN_STOCK',
  ShipToStore = 'SHIP_TO_STORE',
  OutOfStock = 'OUT_OF_STOCK',
  NotFound = 'NOT_FOUND',
}

export enum ReserveStatus {
  InventoryBased = 'reserve-inventory-based',
  ChatWithMobilist = 'reserve-chat-with-mobilist', // DEFAULT
  Always = 'reserve-always',
}

export enum ItemType {
  Phone = 'Phone',
  Accessory = 'Accessory',
}

export enum InventoryItemType {
  phone = 'phone',
  accessory = 'accessory',
}

export enum DeviceType {
  Phone = 'phone',
  Tablet = 'tablet',
  PrepaidPhone = 'prepaid phone',
}

export enum Contentful {
  ContentfulId = 'contentful_id',
  TypeName = '__typename',
}

export enum ContentType {
  ContentfulTextSection = 'ContentfulTextSection',
  ContentfulCombinatorSection = 'ContentfulCombinatorSection',
  ContentfulCardItem = 'ContentfulCardItem',
  ContentfulFaqItem = 'ContentfulFaqItem',
  ContentfulBlade = 'ContentfulBlade',
  ContentfulImageSection = 'ContentfulImageSection',
  ContentfulBanner = 'ContentfulBanner',
  ContentfulPlan = 'ContentfulPlan',
  ContentfulMobilistTip = 'ContentfulMobilistTip',
  ContentfulPhonesSection = 'ContentfulPhonesSection',
  ContentfulCta = 'ContentfulCta',
  ContentfulMultiHeroBanner = 'ContentfulMultiHeroBanner',
  ContentfulTradeInModule = 'ContentfulTradeInModule',
  ContentfulPhone = 'ContentfulPhone',
  ContentfulAccessory = 'ContentfulAccessory',
  ContentfulForm = 'ContentfulForm'
}

export enum ContentHideOption {
  KIOSK = 'KIOSK',
  TMS = 'TMS',
}

export interface KioskProps {
  isKiosk: boolean;
}

export interface GatsbyPageContextProps {
  locale: string;
  urlSlug: string;
  destinationURL?: string;
}

export enum RecaptchaSize {
  NORMAL = 'normal',
  INVISIBLE = 'invisible',
  COMPACT = 'compact',
}

interface ProductInventoryResponseItem {
  storeId?: string;
  productSku?: string;
  status: InventoryStatus;
  productType: InventoryItemType;
  quantity: number;
}

export interface ProductInventoryResponse {
  response: ProductInventoryResponseItem[];
}

export interface DppSkuResponseItem {
  productSku?: string;
  price?: number;
  monthlyFee: number;
  replacementFee: number;
}

interface RawContent {
  raw: string;
}

interface CardDescription {
  cardDescription: string;
}

interface CtaLabel {
  ctaLabel: string;
}

interface TradeInCta {
  ctaTarget?: string;
  ctaType?: string;
  ctaColor?: string;
  ctaLabel?: CtaLabel;
  ctaUrl?: string;
}

interface ImageDescription {
  heading?: RawContent;
  subHeading?: RawContent;
  contentBody?: RawContent;
}

export interface BladeSectionItem {
  __typename: string;
  contentful_id: string;
  heading?: RawContent;
  subHeading?: RawContent;
  contentBody?: RawContent;
  cmsName?: string;
  cardIcon?: Logo;
  cardHeading?: string;
  cardDescription?: CardDescription;
  cardCta?: TradeInCta;
}

export interface TradeInCombinatorSectionItem {
  __typename: string;
  contentful_id: string;
  // TextSection properties
  heading?: RawContent;
  contentBody?: RawContent;
  // Blade properties
  bladeCloseLabel?: string;
  bladeName?: string;
  bladeSections?: BladeSectionItem[];
  // ImageSection properties
  foregroundImageDesktop?: Logo;
  foregroundImageMobile?: Logo;
  foregroundImageTablet?: Logo;
  imageDescription?: ImageDescription;
  // CardItem properties
  cmsName?: string;
  cardIcon?: Logo;
  cardHeading?: string;
  cardDescription?: CardDescription;
  cardCta?: TradeInCta;
  // Form properties
  formFields?: Array<{
    contentful_id: string;
    cmsName?: string;
    displayLabel?: string;
    placeholderLabel?: string;
    errorMessage?: string;
  }>;
  id: string;
}

export interface DppSkuResponse {
  response: DppSkuResponseItem[];
}

export interface ResumatorResponseItem {
  id?: string;
  teamId: string;
  title: string;
  countryId: string;
  city: string;
  state: string;
  zip: string;
  department: string;
  minimumSalary: string;
  maximumSalary: string;
  notes: string;
  originalOpenDate: string;
  type?: string;
  status?: string;
  sendToJobBoards?: string;
  hiringLead: string;
  boardCode: string;
  internalCode: string;
  questionnaire: string;
}

export interface StoreLocationResponse {
  storeId: string;
  address: string;
  postalCode: string;
  city: string;
  province: string;
}

export interface NearbyStore {
  commonInfo: CommonInfo;
  categoryBullseyeIds: string[];
  childLocationIds: Record<string, string>;
  categoryIds: string[];
  storeBanner: StoreBanner;
}

interface CommonInfo {
  lang: string;
  locationName: string;
  lclId: string;
  beId: number;
  tel: string;
  fax: string;
  distance: number;
  address: Address;
  contactInfo: ContactInfo;
  businessHours: BusinessHour[];
  bannerLogoUrl: string;
}

interface Address {
  line1?: string;
  line2?: string;
  line3?: string;
  line4?: string;
  city?: string;
  province?: string;
  country?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
}
interface BusinessHour {
  closed?: boolean;
  openingTime?: string;
  closingTime?: string;
  nameOfDay?: string;
  holidayHours?: boolean;
  holidayName?: string;
}

interface ContactInfo {
  name?: string;
  position?: string;
  tel?: string;
  fax?: string;
  cell?: string;
  eMail?: string;
  url?: string;
}

interface StoreBanner {
  name?: string;
  id?: string;
}

export interface StoreLogo {
  storeName: string;
  storeId?: string;
  logo?: Logo;
  asset?: Asset;
  altText?: string;
}

export interface Asset {
  url?: string;
  localImage?: LocalImage;
  localImageSharp?: LocalImage;
}

interface LocalImage {
  id?: string;
  publicURL?: string;
  publicUrl?: string;
  childImageSharp?: ChildImageSharp | null;
}

interface ChildImageSharp {
  gatsbyImageData?: GatsbyImageData;
}

interface GatsbyImageData {
  layout?: string;
  backgroundColor?: string;
  images?: Images;
  width?: number;
  height?: number;
}

interface Images {
  fallback?: Fallback;
  sources?: Source[];
}

interface Fallback {
  src?: string;
  srcSet?: string;
  sizes?: string;
}

interface Source {
  srcSet?: string;
  type?: string;
  sizes?: string;
}

export interface CartItemLabels {
  upfrontCostLabel: string;
  carrierLabel: string;
  monthlyCostLabel: string;
  pcoPointsLabel: string;
  redeemableForLabel: string;
  valueInOurStoresLabel: string;
  tradeInEstimateLabel: string;
  deviceProtectionPlanAddedLabel: string;
  removeLabel: string;
  accessoryLabel: string;
  phoneLabel: string;
  prepaidPhoneLabel: string;
  tabletLabel: string;
  itemsCountLabel: string;
  itemCountLabel: string;
}

export interface DocumentI {
  raw: string;
}

export interface Phone {
  osType?: string;
  displaySize?: string;
  displayTechnology?: string;
  displayPixelDensity?: string;
  batteryCapacity?: string;
  batteryWirelessCharging: boolean;
  rearCameraResolution?: string;
  rearCameraFeatures?: {
    rearCameraFeatures: string;
  };
  frontCameraResolution?: string;
  frontCameraFeatures?: {
    frontCameraFeatures: string;
  }
  phoneLength?: string;
  phoneWidth?: string;
  phoneThickness?: string;
  phoneWeight?: string;
}

export interface Sorter {
  key: string;
  value: string;
}

export interface TrendingPhonesResponseItem {
  id: string;
  highestPoints?: number;
}

export interface ContentfulRecord {
  contentful_id?: string;
  id?: string;
  __typename?: string;
  raw?: string;
  heading?: string | { raw?: string; heading?: string };
  asset?: Asset;
  altText?: string;
}

export type GenericEventHandler<T = Element> = (event: React.SyntheticEvent<T>) => void;

export type GenericCallback<T = unknown, R = void> = (arg: T) => R;

export type SetStateAction<T> = React.Dispatch<React.SetStateAction<T>>;

export interface GenericEvent extends Event {
  detail?: unknown;
  target: EventTarget | null;
}

/**
 * Generic dictionary type for structured data objects
 */
export interface DataObject<T = string> {
  [key: string]: T;
}

export interface ApiResponse<T = unknown> {
  data?: T;
  status?: number;
  message?: string;
  success?: boolean;
  error?: string | Error;
}

export interface RichTextContent {
  raw?: string;
  references?: Array<{
    id?: string;
    contentful_id?: string;
  }>;
}

export type ComponentRef<T = HTMLElement> = React.RefObject<T>;

export interface ImageAsset {
  asset?: Asset;
  altText?: string;
  url?: string;
}

export type MultiArgFunction = (...args: unknown[]) => unknown;

export interface AccessoryFilterOption {
  name: string;
  key?: string;
  enabled: boolean;
  hexValue?: string;
  assetUrl?: string;
  selected?: boolean;
  count?: number;
}

export interface AccessoryFilter {
  id: string;
  name: string;
  type?: string;
  options: AccessoryFilterOption[];
}

export interface AccessorySortOption {
  id?: string;
  key: string;
  name?: string;
  enabled?: boolean;
  options?: {
    key: string;
    value: string;
    selected?: boolean;
  }[];
}

export interface AccessoryURLParam {
  param: string;
  name: string;
  id: string;
  reduxIndex: number;
  value?: string | null;
}

export interface AccessoryItem {
  id: string;
  contentful_id?: string;
  type: {
    key: string;
    value?: string;
  };
}

export interface AccessoryHeading {
  raw?: string;
  heading?: string;
}

export interface AccessoryVariant {
  manufacturerModel: string;
  price: number;
  id: string;
  displayName: string;
  productSku?: string;
  pcoPoints?: number;
}

export interface InventoryLabel {
  inventoryStatusCode?: string;
}

export interface VariantLabel {
  displayLabel?: string;
  fieldContent?: Array<{
    asset?: {
      url?: string;
      localImage?: {
        childImageSharp?: {
          gatsbyImageData?: {
            images?: {
              sources?: Array<{
                srcSet?: string;
                type?: string;
                sizes?: string;
              }>;
              fallback?: {
                src?: string;
                srcSet?: string;
                sizes?: string;
              };
            };
            width?: number;
            height?: number;
          };
        };
      };
    };
    altText?: string;
    id?: string;
    __typename?: string;
  }>;
}

export type ChatWidgetHandler =
  (event: React.MouseEvent<HTMLButtonElement>, greeting: string) => Promise<void>;

export interface PhoneSelection {
  displayName: string;
}

export interface CtaSection {
  ctaLabel: {
    ctaLabel: string;
  };
  ctaAriaLabel?: string;
  ctaTarget?: string;
  ctaColor?: string;
  ctaUrl?: string;
  ctaHideOption?: string[];
}

export interface PhoneBrandData {
  cmsName?: string;
  displayName?: string;
  contentful_id?: string;
  logo?: {
    asset?: Asset;
    altText?: string;
  };
}

export interface PhoneData {
  displayName: string;
  contentful_id?: string;
  featuredPhoneFrontImage?: {
    asset?: Asset;
    altText?: string;
  };
  phoneVariants?: Array<{
    phoneBrand?: {
      contentful_id?: string;
    };
  }>;
}

export interface PopupData {
  phoneBrands?: PhoneBrandData[];
  phones?: PhoneData[];
}

export type SetShowPopupFunction = (show: boolean) => void;
export type SetPhoneQueryFunction = (query: string) => void;

export interface CarrierImage {
  logo?: {
    asset?: Asset;
    altText?: string;
  };
  displayName?: string;
  cmsName?: string;
  contentful_id?: string;
}

export interface ISelectedCarrier {
  carrierImg: CarrierImage;
  carrierName: string;
  carrierUpfrontCost: number;
  carrierMonthlyCost: number;
  carrierPcoPoints: number;
}

export interface PersonalDetails {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  [key: string]: string | undefined;
}

export interface StoreLocation {
  commonInfo: {
    locationName: string;
    address: {
      line1: string;
      city: string;
      province: string;
      postalCode: string;
    };
  };
}

export interface DppResponse {
  price?: number;
  monthlyFee?: number;
  replacementFee?: number;
}

export interface RecaptchaRef {
  current?: {
    getValue?: () => string;
  };
}

export interface FilterBladeCtaContent {
  ctaLabel?: {
    ctaLabel: string;
  };
  ctaAriaLabel?: string;
}

export type BladeStateFunction = (state: boolean) => void;

export type ClearFiltersFunction = () => void;

export type FilterChangeFunction = <T = unknown, O = unknown>(filter: T, option: O) => void;

export type SorterChangeFunction =
  (sorter: AccessorySortOption, option: { key: string; value: string }) => void;

export type DropdownChangeFunction = <T>(value: T) => void;

export interface ActiveDropdown {
  id: string;
  name: string;
  type?: string;
  options?: Array<{
    name: string;
    enabled: boolean;
    key?: string;
  }>;
}

export type SetActiveDropdownFunction = (dropdown: ActiveDropdown | null) => void;

export interface FilterBladeProps {
  bladeState: boolean;
  setBladeState: BladeStateFunction;
  bladeLabel: string;
  closeLabel: string;
  bladeWidth: number;
  children: React.ReactNode;
  itemsCount: string;
  onClearFilters: ClearFiltersFunction;
  resultCta: FilterBladeCtaContent;
  clearCta: FilterBladeCtaContent;
  filtersList: string;
}

export interface BlogCategory {
  contentful_id: string;
  blogCategoryName: string;
  blogCategoryIcon: {
    asset?: Asset;
    altText?: string;
  };
}

export interface BlogCategories {
  nodes: BlogCategory[];
}

export interface BlogTitle {
  raw?: string;
}

export interface ProductVariant {
  monthlyPrice?: number;
  price?: number;
  isPhoneVariantActive?: boolean;
  isActive?: boolean;
  pcoPoints: number;
  phoneColour?: {
    displayName: string;
    hexValue?: string;
    assetUrl?: string;
  };
  accessoryColour?: {
    displayName: string;
    hexValue?: string;
    assetUrl?: string;
  };
  phoneBrand?: {
    displayName: string;
  };
}

export interface ProductCardData {
  displayName: string;
  urlSlug: string;
  featuredPhoneFrontImage?: {
    asset?: Asset;
    altText?: string;
  };
  heroImageLink?: string;
  phoneVariants?: ProductVariant[];
  variants?: ProductVariant[];
  manufacturerName?: string;
  type?: {
    value: string;
  };
}

interface OneTrustType {
  getGeolocationData: () => {
    countryCode: string;
    stateCode: string;
  } | null;
  ToggleInfoDisplay: () => void;
}

export enum PlacesServiceStatus {
  OK = "OK",
  UNKNOWN_ERROR = "UNKNOWN_ERROR",
  OVER_QUERY_LIMIT = "OVER_QUERY_LIMIT",
  REQUEST_DENIED = "REQUEST_DENIED",
  INVALID_REQUEST = "INVALID_REQUEST",
  ZERO_RESULTS = "ZERO_RESULTS",
  NOT_FOUND = "NOT_FOUND"
}

export enum BusinessStatus {
  OPERATIONAL = "OPERATIONAL",
  CLOSED_TEMPORARILY = "CLOSED_TEMPORARILY",
  CLOSED_PERMANENTLY = "CLOSED_PERMANENTLY"
}

export enum RankBy {
  PROMINENCE = 0,
  DISTANCE = 1
}

export type GoogleMaps = {
  places: {
    PlacesServiceStatus: typeof PlacesServiceStatus;
    BusinessStatus: typeof BusinessStatus;
    RankBy: typeof RankBy;
    Autocomplete: (ref: React.MutableRefObject<HTMLInputElement>, options: {
      types: Array<string>;
      componentRestrictions: Record<string, string>;
    }) => void;
  };
  DirectionsTravelMode: {
    DRIVING: "DRIVING";
    WALKING: "WALKING";
    BICYCLING: "BICYCLING";
    TRANSIT: "TRANSIT";
    TWO_WHEELER: "TWO_WHEELER";
  };
  version: string;
  Geocoder: {
    geocode(
      request: unknown,
      callback?:
        | ((
            a: Array<unknown> | null,
            b: unknown,
          ) => void)
        | null,
    ): Promise<unknown>;
  };
};

export interface TmsWindow extends Window {
  OneTrust?: OneTrustType;
  oneTrustCategoriesChanged?: Event;
  google?: {
    maps: GoogleMaps;
  };
}

export interface GatsbyLocation {
  pathname: string;
  search: string;
  hash: string;
  host: string;
  state?: Record<string, unknown>;
}
